import { useSubscription } from '@apollo/client';
import React from 'react';

import { Content, Separator, Stack } from '@eluve/components';
import { PreVisitRecap } from '@eluve/feature-care-plans';
import { PatientOverview, Situation } from '@eluve/feature-patients';
import {
  listenAppointmentStatusSubscription,
  useAppointmentPatient,
} from '@eluve/frontend-appointment-hooks';
import { AppointmentName, useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { useIsCarePlanFeatureEnabled } from '../../CarePlanFeatureGate';

import {
  EluveAdminControlsPanel,
  EluveAdminControlsProvider,
  EluveAdminControlsToggle,
} from './EluveAdminControls';
import { NewAppointmentForm } from './NewAppointmentForm';
import { shouldShowPreVisitRecap } from './carePlanAppointmentSurface';
import { AppointmentDetails } from './components/AppointmentDetails';

export interface NotStartedAppointmentLayoutProps {
  tenantId: string;
  appointmentId: string;
}

export const NotStartedAppointmentLayout: React.FC<
  NotStartedAppointmentLayoutProps
> = ({ appointmentId, tenantId }) => {
  useSubscription(listenAppointmentStatusSubscription, {
    variables: {
      tenantId,
      appointmentId,
    },
  });

  const patient = useAppointmentPatient();
  const patientOverviews = patient?.patient_overviews ?? [];
  const isPatientOverviewEnabled =
    useIsFeatureFlagEnabled('PATIENT_OVERVIEW') && patientOverviews.length > 0;
  const isPatientSituationEnabled =
    useIsFeatureFlagEnabled('INSIGHTS_PATIENT_SITUATION') &&
    patientOverviews.length > 0;
  const isCarePlanFeatureEnabled = useIsCarePlanFeatureEnabled();

  return (
    <>
      <AppointmentDetails appointmentId={appointmentId} tenantId={tenantId} />
      <Content padded size="sm">
        <Stack gap={8}>
          <EluveAdminControlsProvider>
            <Stack gap={2}>
              <Stack
                direction="horizontal"
                align="center"
                justify="between"
                gap={2}
              >
                <AppointmentName />
                <EluveAdminControlsToggle />
              </Stack>
              <EluveAdminControlsPanel />
            </Stack>
          </EluveAdminControlsProvider>

          {isPatientOverviewEnabled && !isPatientSituationEnabled && (
            <div>
              <div className="overflow-y-auto rounded-lg py-3">
                <PatientOverview includeIcon includeHistory={false} />
              </div>
              <Separator className="my-2" />
            </div>
          )}

          {isPatientSituationEnabled && (
            <div className="overflow-y-auto rounded-lg py-3">
              <Situation />
            </div>
          )}
          {shouldShowPreVisitRecap({
            appointmentStatus: 'NOT_STARTED',
            isCarePlanFeatureEnabled,
          }) && (
            <PreVisitRecap
              tenantId={tenantId}
              appointmentId={appointmentId}
              patientId={patient?.id ?? null}
            />
          )}
          <NewAppointmentForm
            tenantId={tenantId}
            appointmentId={appointmentId}
            isCompactMicrophone={
              isPatientOverviewEnabled || isPatientSituationEnabled
            }
            isSettingsAvailable={
              !(isPatientOverviewEnabled || isPatientSituationEnabled)
            }
          />
        </Stack>
      </Content>
    </>
  );
};
